/**
 * Dialog to configure a custom workflow run from ticket view.
 * Contains: optional Schedule checkbox (Run at + timezone), step-delay rows
 * (expand for per-step delay), Run button. Delay edits apply to this run only.
 * Schedule checkbox defaults off; Play then starts immediately. When checked,
 * Play creates a workflow-start schedule instead.
 *
 * Step list layout: the dialog body scrolls. Rows are block-level and size to
 * content so PSA host CSS cannot flex-shrink labels or expanded delay details.
 */

import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SettingsIcon from "@mui/icons-material/Settings";
import ScheduleDelaySection, {
  type ScheduleDelayOptions,
} from "../../components/customWorkflow/ScheduleDelaySection";
import {
  fromDateTimeLocalInTimeZone,
  getDefaultTimeZone,
  getTimeZoneOptions,
  toDateTimeLocalInTimeZone,
} from "../utils/timezoneUtils";
import {
  buildWorkflowStepDelayOverrides,
  delayDraftFromStepOptions,
  parseWorkflowScheduleSteps,
  validateWorkflowStepDelayDrafts,
  workflowScheduleDelaySummary,
  workflowScheduleStepTitle,
  type WorkflowScheduleStep,
  type WorkflowStepDelayDraft,
  type WorkflowStepDelayOverride,
} from "../utils/workflowScheduleSteps";

export interface ScheduleWorkflowDialogProps {
  open: boolean;
  workflowName: string;
  /** Raw custom-workflow args_json / steps_json used to seed step delay drafts. */
  workflowStepsJson?: unknown;
  submitting?: boolean;
  error?: string | null;
  onClose: () => void;
  onConfirm: (payload: {
    schedule: boolean;
    scheduledFor?: string;
    timezone: string;
    stepDelayOverrides: WorkflowStepDelayOverride[];
  }) => void;
}

/** Step row card. Height must follow content: PSA host pages (Autotask) set
 *  high-specificity `div { display:flex; height:100%; overflow:hidden }` which
 *  otherwise crushes these rows after the dialog portals to document.body. */
const stepRowSx = {
  display: "block !important",
  height: "auto !important",
  maxHeight: "none !important",
  minHeight: "48px !important",
  overflow: "visible !important",
  flex: "none !important",
  flexShrink: "0 !important",
  border: "1px solid #E5E7EB",
  borderRadius: 1,
  bgcolor: "#FFFFFF",
  minWidth: 0,
};

/** Injected with the dialog so Autotask/Halo host CSS cannot shrink step rows
 *  even when this modal portals to document.body outside #neo-assistant-root. */
const NEO_SCHEDULE_DIALOG_CSS = `
#neo-schedule-workflow-dialog .MuiDialog-paper {
  height: auto !important;
  max-height: calc(100vh - 48px) !important;
}
#neo-schedule-workflow-dialog .MuiDialogContent-root {
  height: auto !important;
  max-height: none !important;
  overflow-x: hidden !important;
  overflow-y: auto !important;
}
#neo-schedule-workflow-dialog .neo-schedule-step-list,
#neo-schedule-workflow-dialog .neo-schedule-step-row,
#neo-schedule-workflow-dialog .neo-schedule-step-details {
  display: block !important;
  height: auto !important;
  max-height: none !important;
  overflow: visible !important;
  flex: none !important;
  flex-shrink: 0 !important;
}
#neo-schedule-workflow-dialog .neo-schedule-step-row {
  min-height: 48px !important;
}
#neo-schedule-workflow-dialog .neo-schedule-step-header {
  display: flex !important;
  flex-direction: row !important;
  flex-wrap: nowrap !important;
  align-items: center !important;
  height: auto !important;
  min-height: 48px !important;
  max-height: none !important;
  overflow: visible !important;
  flex: none !important;
  flex-shrink: 0 !important;
}
#neo-schedule-workflow-dialog .neo-schedule-step-header .MuiTypography-root {
  font-size: 14px !important;
  line-height: 1.5 !important;
  min-height: 1.5em !important;
  height: auto !important;
  max-height: none !important;
  overflow-x: hidden !important;
  overflow-y: visible !important;
}
#neo-schedule-workflow-dialog .neo-schedule-step-header .MuiChip-root {
  height: 24px !important;
  min-height: 24px !important;
  max-height: none !important;
  overflow: visible !important;
  flex-shrink: 0 !important;
}
`;

const ScheduleWorkflowDialog: React.FC<ScheduleWorkflowDialogProps> = ({
  open,
  workflowName,
  workflowStepsJson,
  submitting = false,
  error = null,
  onClose,
  onConfirm,
}) => {
  const timezoneOptions = useMemo(() => getTimeZoneOptions(), []);
  const steps = useMemo(() => parseWorkflowScheduleSteps(workflowStepsJson), [workflowStepsJson]);
  const [timezone, setTimezone] = useState(getDefaultTimeZone);
  // Workflow start defaults to now. Do not pass null into toDateTimeLocalInTimeZone — that helper
  // adds one hour for HITL action scheduling.
  const [scheduledForLocal, setScheduledForLocal] = useState(() =>
    toDateTimeLocalInTimeZone(new Date().toISOString(), getDefaultTimeZone())
  );
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [delayDrafts, setDelayDrafts] = useState<WorkflowStepDelayDraft[]>([]);
  const [expandedSteps, setExpandedSteps] = useState<Record<number, boolean>>({});
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const nextTimezone = getDefaultTimeZone();
    setTimezone(nextTimezone);
    setScheduledForLocal(toDateTimeLocalInTimeZone(new Date().toISOString(), nextTimezone));
    setScheduleEnabled(false);
    setDelayDrafts(steps.map((step) => delayDraftFromStepOptions(step.options)));
    setExpandedSteps({});
    setLocalError(null);
  }, [open, steps]);

  const canSubmit = (!scheduleEnabled || Boolean(scheduledForLocal)) && !submitting;

  const patchStepDelay = (index: number, key: keyof ScheduleDelayOptions, value: string) => {
    setDelayDrafts((prev) =>
      prev.map((draft, draftIndex) => (draftIndex === index ? { ...draft, [key]: value } : draft))
    );
  };

  return (
    <Dialog
      open={open}
      onClose={() => !submitting && onClose()}
      maxWidth="md"
      fullWidth
      disableEnforceFocus
      disableAutoFocus
      disableRestoreFocus
      disableScrollLock
      id="neo-schedule-workflow-dialog"
      className="neo-schedule-workflow-dialog"
      PaperProps={{
        className: "neo-schedule-workflow-paper",
        sx: {
          height: "auto !important",
          maxHeight: "calc(100vh - 48px) !important",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* Configure dialog: gear title, optional Schedule checkbox, Run primary.
          disableEnforceFocus: Autotask/WalkMe also trap document focus; MUI's
          trap + theirs overflows the stack when this nested dialog closes. */}
      <style>{NEO_SCHEDULE_DIALOG_CSS}</style>
      <DialogTitle sx={{ fontFamily: "var(--theo-font-sans)", fontWeight: 700, display: "flex", alignItems: "center", gap: 1 }}>
        <SettingsIcon sx={{ color: "#705D4A" }} />
        Configure {workflowName || "workflow"}
      </DialogTitle>
      <DialogContent
        sx={{
          overflowY: "auto",
          overflowX: "hidden",
          height: "auto",
          flex: "1 1 auto",
          minHeight: 0,
        }}
      >
        <Stack spacing={2} sx={{ pt: 1, height: "auto", flex: "none" }}>
          {(localError || error) ? (
            <Typography sx={{ fontFamily: "var(--theo-font-sans)", color: "#B91C1C", fontSize: 13 }}>{localError || error}</Typography>
          ) : null}
          <FormControlLabel
            control={
              <Checkbox
                checked={scheduleEnabled}
                onChange={(event) => setScheduleEnabled(event.target.checked)}
                sx={{ color: "#705D4A", "&.Mui-checked": { color: "#705D4A" } }}
              />
            }
            label={
              <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 14, fontWeight: 600, color: "#19213D" }}>
                Schedule
              </Typography>
            }
          />
          {scheduleEnabled ? (
            <>
              <TextField
                label="Run at"
                type="datetime-local"
                value={scheduledForLocal}
                onChange={(event) => setScheduledForLocal(event.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
                sx={{ "& .MuiInputBase-root": { fontFamily: "var(--theo-font-sans)" }, "& .MuiInputLabel-root": { fontFamily: "var(--theo-font-sans)" } }}
              />
              <Autocomplete
                options={timezoneOptions}
                value={timezone}
                onChange={(_event, value) => setTimezone(value || getDefaultTimeZone())}
                renderInput={(params) => <TextField {...params} label="Timezone" fullWidth />}
                fullWidth
              />
            </>
          ) : null}
          <Box>
            <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontWeight: 600, fontSize: 14, color: "#19213D", mb: 0.5 }}>
              Step delays
            </Typography>
            <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 12, color: "#71717A", mb: 1 }}>
              These delays apply only to this run and do not update the saved workflow.
            </Typography>
            {steps.length === 0 ? (
              <Typography sx={{ fontFamily: "var(--theo-font-sans)", fontSize: 13, color: "#71717A" }}>
                Step delays will use the saved workflow when this run starts.
              </Typography>
            ) : (
              <Box
                className="neo-schedule-step-list"
                sx={{
                  display: "block !important",
                  height: "auto !important",
                  maxHeight: "none !important",
                  overflow: "visible !important",
                  flex: "none !important",
                  pr: 0.5,
                }}
              >
                {steps.map((step: WorkflowScheduleStep, index) => {
                  const draft = delayDrafts[index] || delayDraftFromStepOptions(step.options);
                  const delayLabel = workflowScheduleDelaySummary(draft);
                  const expanded = Boolean(expandedSteps[index]);
                  const toggleExpanded = () => {
                    setExpandedSteps((prev) => ({ ...prev, [index]: !prev[index] }));
                  };
                  return (
                    <Box
                      key={step.id || `${step.action}-${index}`}
                      className="neo-schedule-step-row"
                      sx={{ ...stepRowSx, mb: 1, "&:last-of-type": { mb: 0 } }}
                    >
                      <Box
                        className="neo-schedule-step-header"
                        role="button"
                        tabIndex={0}
                        aria-expanded={expanded}
                        onClick={toggleExpanded}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            toggleExpanded();
                          }
                        }}
                        sx={{
                          display: "flex !important",
                          flexDirection: "row !important",
                          flexWrap: "nowrap !important",
                          alignItems: "center !important",
                          gap: 1,
                          width: "100%",
                          boxSizing: "border-box",
                          cursor: "pointer",
                          px: 1.5,
                          py: 1.25,
                          height: "auto !important",
                          minHeight: "48px !important",
                          overflow: "visible !important",
                          flex: "none !important",
                        }}
                      >
                        <Typography
                          sx={{
                            fontFamily: "var(--theo-font-sans)",
                            fontSize: "14px !important",
                            fontWeight: 600,
                            lineHeight: "1.5 !important",
                            minHeight: "1.5em !important",
                            height: "auto !important",
                            color: "#19213D",
                            flex: "1 1 auto",
                            minWidth: 0,
                            overflowX: "hidden",
                            overflowY: "visible !important",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {workflowScheduleStepTitle(step, index)}
                        </Typography>
                        <Chip
                          size="small"
                          label={delayLabel || "No delay"}
                          sx={{
                            fontFamily: "var(--theo-font-sans)",
                            fontSize: 11,
                            height: 24,
                            minHeight: 24,
                            flexShrink: 0,
                            borderRadius: "4px",
                            bgcolor: delayLabel ? "#F3EEE8" : "#F4F4F5",
                            color: delayLabel ? "#705D4A" : "#71717A",
                          }}
                        />
                        <ExpandMoreIcon
                          sx={{
                            fontSize: 20,
                            color: "#71717A",
                            flexShrink: 0,
                            transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                            transition: (theme) =>
                              theme.transitions.create("transform", {
                                duration: theme.transitions.duration.shorter,
                              }),
                          }}
                        />
                      </Box>
                      {expanded ? (
                        <Box
                          className="neo-schedule-step-details"
                          sx={{
                            display: "block !important",
                            height: "auto !important",
                            maxHeight: "none !important",
                            overflow: "visible !important",
                            flex: "none !important",
                            borderTop: "1px solid #E5E7EB",
                            p: 1.5,
                            bgcolor: "#FAFAF9",
                          }}
                        >
                          <ScheduleDelaySection
                            enabled
                            options={draft}
                            onChange={(key, value) => patchStepDelay(index, key, value)}
                          />
                        </Box>
                      ) : null}
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={submitting} sx={{ textTransform: "none", fontFamily: "var(--theo-font-sans)" }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!canSubmit}
          onClick={() => {
            try {
              const delayError = validateWorkflowStepDelayDrafts(delayDrafts);
              if (delayError) {
                setLocalError(delayError);
                return;
              }
              setLocalError(null);
              onConfirm({
                schedule: scheduleEnabled,
                scheduledFor: scheduleEnabled
                  ? fromDateTimeLocalInTimeZone(scheduledForLocal, timezone)
                  : undefined,
                timezone,
                stepDelayOverrides: buildWorkflowStepDelayOverrides(steps, delayDrafts),
              });
            } catch (err: any) {
              setLocalError(err?.message || "Run time must be a valid datetime");
            }
          }}
          sx={{ textTransform: "none", fontFamily: "var(--theo-font-sans)", backgroundColor: "#705D4A", "&:hover": { backgroundColor: "#5c4c3d" } }}
        >
          {submitting ? (scheduleEnabled ? "Scheduling…" : "Starting…") : "Run"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ScheduleWorkflowDialog;
